import React, { useEffect, useContext, useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Modal,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import Account from "./Account";
import Context from "context/UserContext";
import api from "services/accouts";
import Seller from "./Seller";
import Note from "./Note";
import PasswordReset from "./PasswordReset";
import Swal from "sweetalert2";
import * as FileSaver from "file-saver";
import * as XLSX from "sheetjs-style";
import { usePagination } from "hooks/usePagination";

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const SEARCH_FIELDS = ["nombre_completo", "dealer_id"];

export default function Index() {
  const [cuentas, setCuentas] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [show, setShow] = useState(false);
  const [seller, setSeller] = useState(null);
  const [opt, setOpt] = useState(1);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [savingPassword, setSavingPassword] = useState(false);
  const [pendingPassword, setPendingPassword] = useState(null);

  const { token } = useContext(Context);

  const {
    paginatedItems,
    currentPage,
    totalPages,
    itemsPerPage,
    searchQuery,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    setSearchQuery,
    setItemsPerPage,
  } = usePagination(cuentas, { searchFields: SEARCH_FIELDS });

  const fetchAccounts = () => {
    setLoadingAccounts(true);
    return api
      .accouts(token)
      .then(({ data }) => setCuentas(data))
      .catch(() => {
        Swal.fire({
          icon: "error",
          title: "Error",
          confirmButtonColor: "#3b82f6",
          text: "No se pudo cargar el listado de vendedores. Intenta de nuevo.",
        });
      })
      .finally(() => setLoadingAccounts(false));
  };

  useEffect(() => {
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const refreshAccounts = () => fetchAccounts();

  const handleClose = () => setShow(false);

  const delete_account = (id) => {
    if (!window.confirm("¿Estás seguro de eliminar?")) return;
    api.deleteUser(token, id).then(refreshAccounts);
  };

  const active_account = (id, value) => {
    const action = value === "1" ? "desactivar" : "activar";
    if (!window.confirm(`¿Estás seguro de ${action} la cuenta?`)) return;
    api.activeUser(token, id, !(value === "1")).then(refreshAccounts);
  };

  const handleModal = (id) => {
    setOpt(1);
    setShow(true);
    setSeller(cuentas.find((item) => item.id === id));
  };

  const addNote = (id) => {
    setOpt(2);
    setShow(true);
    setSeller(cuentas.find((item) => item.id === id));
  };

  const handleSave = (id, note) => {
    api.addNoteUser(token, id, note).then(() => {
      refreshAccounts();
      Swal.fire({
        icon: "success",
        title: "Éxito",
        confirmButtonColor: "#3b82f6",
        text: "Se ha actualizado exitosamente.",
      });
      handleClose();
    });
  };

  const handleOpenPasswordModal = (id) => {
    setPendingPassword(null);
    setPasswordUser(cuentas.find((item) => item.id === id));
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordUser(null);
    setPendingPassword(null);
  };

  const handleClosePasswordModal = () => {
    if (!pendingPassword) {
      closePasswordModal();
      return;
    }
    Swal.fire({
      icon: "question",
      title: "Contraseña sin guardar",
      text: "Generaste una nueva contraseña que aún no has guardado. ¿Deseas guardarla antes de cerrar?",
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: "Guardar y cerrar",
      denyButtonText: "Cerrar sin guardar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#3b82f6",
      denyButtonColor: "#6c757d",
      cancelButtonColor: "#adb5bd",
    }).then((result) => {
      if (result.isConfirmed) {
        handleSavePassword(passwordUser.id, pendingPassword);
      } else if (result.isDenied) {
        closePasswordModal();
      }
    });
  };

  const handleSavePassword = (id, password) => {
    setSavingPassword(true);
    api
      .resetUserPassword(token, id, password)
      .then(() => {
        closePasswordModal();
        Swal.fire({
          icon: "success",
          title: "Contraseña actualizada",
          confirmButtonColor: "#3b82f6",
          text: "La contraseña se ha guardado correctamente.",
        });
      })
      .catch(() => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo actualizar la contraseña. Intenta de nuevo.",
        });
      })
      .finally(() => setSavingPassword(false));
  };

  const handleRoleChange = (id, newRole, nombre) => {
    if (newRole === "Promotor") {
      Swal.fire({
        icon: "warning",
        title: "¿Cambiar rol a EDM?",
        html: `
          <p>Estás a punto de cambiar el rol de <strong>${nombre}</strong>.</p>
          <p>Al confirmar:</p>
          <ul style="text-align:left; margin: 0 auto; display: inline-block;">
            <li>El usuario <strong>ya no aparecerá</strong> en la lista de Vendedores.</li>
            <li>Pasará a la sección de <strong>EDM</strong>.</li>
          </ul>
        `,
        showCancelButton: true,
        confirmButtonColor: "#e67e22",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Sí, cambiar a EDM",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (!result.isConfirmed) return;
        api
          .changeUserRole(token, id, newRole)
          .then(() => {
            refreshAccounts();
            Swal.fire({
              icon: "success",
              title: "Rol actualizado",
              confirmButtonColor: "#3b82f6",
              text: `${nombre} ahora aparece en la sección EDM.`,
            });
          })
          .catch(() => {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "No se pudo cambiar el rol. Intenta de nuevo.",
            });
          });
      });
    } else {
      Swal.fire({
        icon: "question",
        title: "¿Cambiar rol a Vendedor?",
        text: `${nombre} volverá a aparecer en la lista de Vendedores.`,
        showCancelButton: true,
        confirmButtonColor: "#28a745",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Sí, cambiar",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (!result.isConfirmed) return;
        api
          .changeUserRole(token, id, newRole)
          .then(() => {
            refreshAccounts();
            Swal.fire({
              icon: "success",
              title: "Rol actualizado",
              confirmButtonColor: "#3b82f6",
              text: `${nombre} ahora aparece como Vendedor.`,
            });
          })
          .catch(() => {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "No se pudo cambiar el rol. Intenta de nuevo.",
            });
          });
      });
    }
  };

  const exportToExcel = () => {
    if (loadingAccounts || exporting) return;

    if (cuentas.length === 0) {
      Swal.fire({
        icon: "info",
        title: "Sin datos",
        confirmButtonColor: "#3b82f6",
        text: "No hay cuentas de vendedores para exportar.",
      });
      return;
    }

    setExporting(true);
    // Se difiere al siguiente ciclo para que el botón alcance a pintar el
    // spinner antes de que el hilo principal se bloquee generando el archivo.
    setTimeout(() => {
      try {
        const exportData = cuentas.map((item) => ({
          ID: item.id,
          Nombre: item.nombre,
          "Apellido paterno": item.apellido_paterno,
          "Apellido materno": item.apellido_materno,
          "Nombre completo": item.nombre_completo,
          Email: item.email,
          Celular: item.celular,
          Género: item.genero,
          "Padre/Madre": item.progenitor,
          "Fecha de nacimiento": item.cumpleanos,
          "# SAP": item.dealer_id,
          Distribuidor: item.matriz?.nombre_comercial ?? "",
          Escolaridad: item.escolaridad,
          Rol: item.role === "Promotor" ? "EDM" : item.role,
          Status: item.status === 1 ? "Activo" : "Inactivo",
          Nota: item.nota,
          "Fecha de registro": item.created_at,
        }));
        const fileType =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        FileSaver.saveAs(
          new Blob([excelBuffer], { type: fileType }),
          "Reporte-de-vendedores.xlsx"
        );
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          confirmButtonColor: "#3b82f6",
          text: "No se pudo generar el archivo. Intenta de nuevo.",
        });
      } finally {
        setExporting(false);
      }
    }, 0);
  };

  const pageNumbers = () => {
    const delta = 2;
    const range = [];
    const result = [];
    let prev;

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        range.push(i);
      }
    }

    for (const page of range) {
      if (prev && page - prev === 2) {
        result.push(prev + 1);
      } else if (prev && page - prev !== 1) {
        result.push("...");
      }
      result.push(page);
      prev = page;
    }

    return result;
  };

  return (
    <Container className="mt-5">
      <Row className="align-items-center mb-4">
        <Col md={6}>
          <h1>Listado de vendedores</h1>
        </Col>
        <Col md={6} className="text-end">
          <Button
            variant="primary"
            onClick={exportToExcel}
            disabled={loadingAccounts || exporting}
          >
            {loadingAccounts ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Cargando cuentas...
              </>
            ) : exporting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Generando archivo...
              </>
            ) : (
              "Descarga de cuentas vendedores"
            )}
          </Button>
        </Col>
      </Row>

      <Row className="align-items-center mb-3 g-2">
        <Col md={6}>
          <Form.Control
            type="search"
            placeholder="Buscar por nombre o # SAP..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Buscar vendedor"
          />
        </Col>
        <Col md={3}>
          <Form.Select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            aria-label="Registros por página"
          >
            {ITEMS_PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} por página
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3} className="text-end text-muted small">
          {totalItems > 0
            ? `Mostrando ${startIndex}–${endIndex} de ${totalItems} resultados`
            : "Sin resultados"}
        </Col>
      </Row>

      <Table striped bordered hover responsive>
        <thead style={{ background: "rgb(237, 113, 0)", color: "#fff" }}>
          <tr>
            <th>#</th>
            <th>Nombre</th>
            <th># SAP</th>
            <th>Rol</th>
            <th>Información</th>
            <th>Activar cuenta</th>
            <th>Seguimiento</th>
            <th>Nota</th>
            <th>Fecha de registro</th>
            <th>Contraseña</th>
          </tr>
        </thead>
        <tbody>
          {paginatedItems.length > 0 ? (
            paginatedItems.map((item, i) => (
              <Account
                key={item.id}
                num={(currentPage - 1) * itemsPerPage + i + 1}
                {...item}
                onDelete={delete_account}
                onActive={active_account}
                onAddNote={addNote}
                handleModal={handleModal}
                onRoleChange={handleRoleChange}
                onResetPassword={handleOpenPasswordModal}
              />
            ))
          ) : (
            <tr>
              <td colSpan={10} className="text-center py-4 text-muted">
                No se encontraron resultados
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            <Pagination.First
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              aria-label="Primera página"
            />
            <Pagination.Prev
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Página anterior"
            />
            {pageNumbers().map((page, i) =>
              page === "..." ? (
                <Pagination.Ellipsis key={`ellipsis-${i}`} disabled />
              ) : (
                <Pagination.Item
                  key={page}
                  active={page === currentPage}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </Pagination.Item>
              )
            )}
            <Pagination.Next
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="Página siguiente"
            />
            <Pagination.Last
              onClick={() => goToPage(totalPages)}
              disabled={currentPage === totalPages}
              aria-label="Última página"
            />
          </Pagination>
        </div>
      )}

      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{opt === 1 ? "Información" : "Notas"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {opt === 1 ? (
            <Seller info={seller} />
          ) : (
            <Note info={seller} handleSave={handleSave} />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPasswordModal} onHide={handleClosePasswordModal}>
        <Modal.Header closeButton>
          <Modal.Title>Recuperar contraseña</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <PasswordReset
            info={passwordUser}
            onSave={handleSavePassword}
            onGenerate={setPendingPassword}
            saving={savingPassword}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClosePasswordModal}
            disabled={savingPassword}
          >
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
